/**
 * CTRL_14 Plugin - Data Minimization
 * Custom UI with data usage analytics, redundancy detection, and retention tracking
 * Extends GenericConfigPanel with minimization metrics
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Alert,
  Table,
  Tag,
  Space,
  Progress,
  Tooltip,
} from 'antd';
import {
  ShrinkOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import GenericConfigPanel from '../GenericConfigPanel';
import { colors } from '../../../../theme/designTokens';

/**
 * Data Minimization Dashboard Component
 */
function DataMinimizationDashboard({ config, projectId }) {
  const [stats, setStats] = useState({
    totalFields: 0,
    activeFields: 0,
    unusedFields: 0,
    retentionCompliance: 0,
  });
  const [fieldAnalysis, setFieldAnalysis] = useState([]);

  useEffect(() => {
    if (config?.enabled && projectId) {
      fetchMinimizationData();
    }
  }, [config, projectId]);

  const fetchMinimizationData = async () => {
    // Mock data - replace with actual API call
    setTimeout(() => {
      setStats({
        totalFields: 45,
        activeFields: 32,
        unusedFields: 13,
        retentionCompliance: 94.2,
      });
      setFieldAnalysis([
        {
          id: 1,
          field: 'user_email',
          usage: 'high',
          lastAccessed: new Date().toISOString(),
          status: 'necessary',
        },
        {
          id: 2,
          field: 'user_phone',
          usage: 'medium',
          lastAccessed: new Date(Date.now() - 86400000).toISOString(),
          status: 'necessary',
        },
        {
          id: 3,
          field: 'user_hobby',
          usage: 'none',
          lastAccessed: new Date(Date.now() - 7776000000).toISOString(),
          status: 'redundant',
        },
        {
          id: 4,
          field: 'temp_session_id',
          usage: 'low',
          lastAccessed: new Date(Date.now() - 2592000000).toISOString(),
          status: 'expired',
        },
      ]);
    }, 500);
  };

  const columns = [
    {
      title: 'Field Name',
      dataIndex: 'field',
      key: 'field',
    },
    {
      title: 'Usage',
      dataIndex: 'usage',
      key: 'usage',
      render: (usage) => {
        const colorMap = {
          high: 'green',
          medium: 'blue',
          low: 'orange',
          none: 'red',
        };
        return <Tag color={colorMap[usage]}>{usage.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const config = {
          necessary: { color: 'green', icon: <CheckCircleOutlined /> },
          redundant: { color: 'orange', icon: <WarningOutlined /> },
          expired: { color: 'red', icon: <DeleteOutlined /> },
        };
        const { color, icon } = config[status] || {};
        return (
          <Tag color={color} icon={icon}>
            {status.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: 'Last Accessed',
      dataIndex: 'lastAccessed',
      key: 'lastAccessed',
      render: (ts) => {
        const daysAgo = Math.floor((Date.now() - new Date(ts)) / 86400000);
        return `${daysAgo} days ago`;
      },
    },
  ];

  if (!config?.enabled) {
    return (
      <Alert
        message="Data Minimization Disabled"
        description="Enable to track and reduce unnecessary data collection and retention."
        type="info"
        showIcon
      />
    );
  }

  const utilizationRate = stats.totalFields > 0 ? ((stats.activeFields / stats.totalFields) * 100).toFixed(1) : 0;
  const redundancyRate = stats.totalFields > 0 ? ((stats.unusedFields / stats.totalFields) * 100).toFixed(1) : 0;

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Minimization Statistics */}
      <Row gutter={16}>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title={<Tooltip title="Fields actively used in model"><Space>Active Fields <InfoCircleOutlined /></Space></Tooltip>}
              value={stats.activeFields}
              suffix={`/ ${stats.totalFields}`}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: colors.status.pass }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Redundant Fields"
              value={stats.unusedFields}
              prefix={<WarningOutlined />}
              valueStyle={{ color: colors.status.warning }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Utilization Rate"
              value={utilizationRate}
              suffix="%"
              prefix={<ShrinkOutlined />}
            />
            <Progress
              percent={utilizationRate}
              strokeColor={utilizationRate >= 80 ? colors.status.pass : colors.status.warning}
              size="small"
              showInfo={false}
              style={{ marginTop: 8 }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Retention Compliance"
              value={stats.retentionCompliance}
              suffix="%"
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: colors.status.pass }}
            />
          </Card>
        </Col>
      </Row>

      {/* Field Analysis */}
      <div>
        <h4>Field Analysis</h4>
        <Table
          columns={columns}
          dataSource={fieldAnalysis}
          rowKey="id"
          size="small"
          pagination={{ pageSize: 10 }}
        />
      </div>

      {stats.unusedFields > 0 && (
        <Alert
          message={`${stats.unusedFields} redundant field(s) detected`}
          description={`Removing unused fields can reduce data footprint by ${redundancyRate}%`}
          type="warning"
          showIcon
          icon={<WarningOutlined />}
        />
      )}
    </Space>
  );
}

/**
 * CTRL_14 Plugin Component
 */
function CTRL_14_Plugin({ projectId, controlCode }) {
  const [config, setConfig] = useState({
    enabled: false,
    retention_days: 90,
  });

  return (
    <div>
      {/* Minimization Dashboard */}
      <DataMinimizationDashboard config={config} projectId={projectId} />

      {/* Generic Config Panel - usePlugin={false} prevents infinite loop */}
      <GenericConfigPanel projectId={projectId} controlCode={controlCode} usePlugin={false} />
    </div>
  );
}

export default CTRL_14_Plugin;
